'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'

import { useState } from 'react'
import { cn } from '@/utils/twMerge'
import type { Settings } from 'react-slick'
import { PrismicRichText } from '@prismicio/react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import '@/styles/vendors/slickCarousel'
import '@/styles/vendors/reactTabs'
import { sliderSettingsWithoutDot } from '@/utils/sliderSettings'
import { useOnScrollActiveTab } from '@/hooks/useOnScrollActiveTab'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

const SecurityPostureComponent = ({ data, uid }: { data: DataObject; uid?: string }) => {
  const [tabIndex, setTabIndex] = useState(0)
  useOnScrollActiveTab(setTabIndex)

  return (
    <section className={cn('relative z-[1] pb-20 pt-20 lg:pb-36', uid === 'frameworks' ? '!p-0' : '')}>
      <div className="container">
        <div className="text-center lg:mx-auto lg:max-w-5xl">
          <PrismicRichText field={data.primary.title} />
          <div className="mb-12">
            <PrismicRichText field={data.primary.description} />
          </div>

          {data.primary.btn_label && (
            <PrismicNextLink field={data.primary.btn_link} className="btn btn-primary btn-default btn-interaction">
              {data.primary.btn_label}
              <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
            </PrismicNextLink>
          )}
        </div>

        {data.items.length > 0 && (
          <>
            {/* Desktop Version */}
            <div className="hidden pt-20 lg:block lg:pt-24">
              <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
                <TabList className="m-0 grid grid-cols-2 gap-8 border-b-0">
                  {data.items.map((item: ItemObject, index: number) => (
                    <Tab
                      key={item.title}
                      id={`tab-${index}`}
                      className="focus:ring-primary mb-8 rounded-md border-0 px-6 pb-8 pt-6 shadow-lg backdrop-blur-sm transition-shadow duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-opacity-50">
                      <span className="text-sm font-semibold">{item.title}</span>
                      <div className="mt-2 text-[22px] font-light">
                        <PrismicRichText field={item.description} />
                      </div>
                    </Tab>
                  ))}

                  <div className="mb-12">
                    <PrismicRichText field={data.primary.sub_title} />
                  </div>

                  {data.primary.get_started_btn_label && (
                    <PrismicNextLink
                      field={data.primary.get_started_btn_link}
                      className="bg-primary hover:bg-primary-dark mt-8 inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold text-white transition duration-300">
                      {data.primary.get_started_btn_label}
                      <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                    </PrismicNextLink>
                  )}
                </TabList>

                {data.items.map((item: ItemObject, index: number) => (
                  <TabPanel key={`i-${index}`} className="animate-fadeIn">
                    <PrismicNextImage field={item.logo} className="sticky top-24 rounded-md" fallbackAlt="" />
                  </TabPanel>
                ))}
              </Tabs>
            </div>

            {/* Mobile Version */}
            <div className="mt-10 block lg:hidden">
              <Slider {...sliderSettingsWithoutDot}>
                {data.items.map((item: ItemObject) => (
                  <div
                    key={item.title}
                    className="grid grid-cols-1 rounded-md border border-gray-200 bg-white/90 shadow-lg backdrop-blur-sm">
                    <div
                      className="h-[350px] rounded-t-md bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${item.logo.url})` }}></div>
                    <div className="min-h-[320px] px-4 pb-6 pt-4 sm:min-h-[200px] md:min-h-[320px]">
                      <span className="text-sm font-semibold">{item.title}</span>
                      <div className="mt-2 text-xl font-light">
                        <PrismicRichText field={item.description} />
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>

              <div className="mt-8">
                <PrismicRichText field={data.primary.sub_title} />
              </div>

              {data.primary.get_started_btn_label && (
                <PrismicNextLink
                  field={data.primary.get_started_btn_link}
                  className="bg-primary hover:bg-primary-dark mt-8 inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold text-white transition duration-300">
                  {data.primary.get_started_btn_label}
                  <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                </PrismicNextLink>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default SecurityPostureComponent
